import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { APIProvider, Map, AdvancedMarker, Pin, useMap } from "@vis.gl/react-google-maps";
import { useBirdRadarStore } from "./store/RadarStore";
import SpeciesCard from "./components/SpeciesCard";
import { RadarSpeciesDto } from "./store/RadarStore";
import SavedLocationsPanel from "./components/SavedLocationsPanel";
import { useAuthStore } from "./store/AuthStore";
import { useTourStore, TourTargetLocationDto } from "./store/TourStore";
import TourPlannerPanel from "./components/TourPlannerPanel";

// Renders target tour hotspots with species photo marker badges
function TargetOverlays({ locations, selectedLocationId, onSpeciesClick }: { 
  locations: TourTargetLocationDto[],
  selectedLocationId: string | null,
  onSpeciesClick: (speciesId: number) => void
}) {
  return (
    <>
      {locations.map((loc, locIdx) => {
        const isSelected = loc.locationId === selectedLocationId;
        const topSpecies = loc.matchingSpecies?.[0];
        const photoUrl = topSpecies?.photoUrl;
        const color = isSelected ? '#6366f1' : (topSpecies?.themeColor || '#10b981');
        const size = isSelected ? 56 : 44;

        return (
          <AdvancedMarker
            key={`${loc.locationId}-${locIdx}`}
            position={{ lat: loc.latitude, lng: loc.longitude }}
            title={`${loc.locationName} · ${loc.distanceKm.toFixed(1)}km · ${loc.matchingSpecies?.length || 0} species`}
            zIndex={isSelected ? 999 : undefined}
          >
            <div 
              style={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => topSpecies && onSpeciesClick(topSpecies.speciesId)}
            >
              {/* Pulsing glow ring for selected */}
              {isSelected && (
                <div style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: size + 20, height: size + 20,
                  borderRadius: '50%',
                  border: '2px solid #6366f1',
                  animation: 'ping 1.2s cubic-bezier(0, 0, 0.2, 1) infinite',
                  opacity: 0.6,
                  pointerEvents: 'none'
                }} />
              )}
              {photoUrl ? (
                <div style={{
                  width: size, height: size, borderRadius: '50%',
                  border: `${isSelected ? 4 : 3}px solid ${color}`,
                  overflow: 'hidden',
                  boxShadow: isSelected ? `0 0 0 3px #6366f180, 0 0 20px #6366f180` : `0 0 10px ${color}80`,
                  background: '#111',
                  transition: 'all 0.2s ease'
                }}>
                  <img src={photoUrl} alt={topSpecies?.commonName} 
                       style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ) : (
                <div style={{
                  width: size, height: size, borderRadius: '50%',
                  border: `${isSelected ? 4 : 3}px solid ${color}`,
                  background: isSelected ? '#1e1b4b' : '#1a2a1a',
                  display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', fontSize: isSelected ? 24 : 20,
                  boxShadow: isSelected ? `0 0 0 3px #6366f180, 0 0 20px #6366f180` : `0 0 10px ${color}80`,
                  transition: 'all 0.2s ease'
                }}>🦜</div>
              )}
              {/* Species count badge */}
              {loc.matchingSpecies && loc.matchingSpecies.length > 1 && (
                <div style={{
                  position: 'absolute', top: -6, right: -6,
                  background: color, color: 'white', borderRadius: '50%',
                  width: 18, height: 18, fontSize: 10, fontWeight: 'bold',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1.5px solid #111'
                }}>
                  {loc.matchingSpecies.length}
                </div>
              )}
              {/* Location label for selected */}
              {isSelected && (
                <div style={{
                  position: 'absolute', top: size + 6, left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#6366f1', color: 'white',
                  fontSize: 10, fontWeight: 600,
                  padding: '2px 8px', borderRadius: 6,
                  whiteSpace: 'nowrap', maxWidth: 140,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  boxShadow: '0 2px 8px #6366f180'
                }}>
                  {loc.locationName}
                </div>
              )}
              {/* Stem */}
              <div style={{
                width: 2, height: isSelected ? 12 : 8, background: color,
                margin: '0 auto', opacity: 0.8, transition: 'all 0.2s ease'
              }} />
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
}

// Renders using the Google Maps Circle and Marker overlays
function RadarOverlays({
  lat, lng, radiusKm, birds, onSelect
}: {
  lat: number;
  lng: number;
  radiusKm: number;
  birds: RadarSpeciesDto[];
  onSelect: (speciesId: number) => void;
}) {
  const map = useMap();
  const circleRef = useRef<google.maps.Circle | null>(null);

  // Pan map when location changes
  useEffect(() => {
    if (map) {
      map.panTo({ lat, lng });
    }
  }, [map, lat, lng]);

  // Radar circle — updates when lat/lng/radius changes
  useEffect(() => {
    if (!map) return;
    if (circleRef.current) circleRef.current.setMap(null);
    circleRef.current = new google.maps.Circle({
      map,
      center: { lat, lng },
      radius: radiusKm * 1000,  // convert km → meters
      strokeColor: "#3b82f6",
      strokeOpacity: 0.7,
      strokeWeight: 2,
      fillColor: "#3b82f6",
      fillOpacity: 0.07,
    });
    return () => { circleRef.current?.setMap(null); };
  }, [map, lat, lng, radiusKm]);

  return (
    <>
      {/* Species sighting markers */}
      {birds.flatMap((bird) => {
        const photoUrl = bird.topPhotos?.[0]?.imageUrl;
        const color = bird.themeColors?.[0] || "#10b981";

        return (bird.sightings ?? []).map((sighting, i) => (
          <AdvancedMarker
            key={`${bird.speciesId}-${i}`}
            position={{ lat: sighting.latitude, lng: sighting.longitude }}
            title={`${bird.primaryCommonName} · [${sighting.source === 'inat' ? '🌿 iNat' : '🦅 eBird'}] ${sighting.locationName}${sighting.howMany > 0 ? ` · ${sighting.howMany} seen` : ""}`}
            onClick={() => onSelect(bird.speciesId)}
          >
            <div style={{ position: "relative" }}>
              {/* Bird photo thumbnail circle */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: `3px solid ${color}`,
                  boxShadow: `0 2px 8px rgba(0,0,0,0.55), 0 0 0 1.5px white`,
                  overflow: "hidden",
                  cursor: "pointer",
                  background: `${color}33`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={bird.primaryCommonName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: 16, fontWeight: 700, color }}>
                    {bird.primaryCommonName.charAt(0)}
                  </span>
                )}
              </div>

              {/* BRANDING BADGE */}
              <div style={{
                position: "absolute",
                bottom: 2, right: -4,
                background: sighting.source === 'inat' ? '#10b981' : '#3b82f6',
                color: '#fff',
                fontSize: 10,
                width: 18, height: 18,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                border: '1.5px solid white'
              }}>
                {sighting.source === 'inat' ? '🌿' : '🦅'}
              </div>

              {/* Callout tail pointing down */}
              <div
                style={{
                  width: 0, height: 0,
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: `7px solid ${color}`,
                  margin: "0 auto",
                }}
              />
            </div>
          </AdvancedMarker>
        ));
      })}
    </>
  );
}

export const UI_TEXT: Record<string, Record<string, string>> = {
  en: {
    activeRadar: "Active Radar",
    radius: "radius",
    scanning: "Scanning",
    speciesDetected: "species detected",
    noSightings: "No sightings found",
    clickDifferent: "Click on the map to scan a different area.",
    radarRadius: "Radar Radius",
    locating: "Locating coordinates...",
    clickScan: "Click anywhere on the map to scan radar.",
    menuSettings: "Settings",
    menuSaved: "Saved Locations",
    menuAbout: "About Birdetector",
    aboutSubtitle: "Global Bird Radar PWA",
    aboutDesc: "A real-time birding radar powered by the GBW geo-spatial backend. Discover local species instantly with data from eBird and iNaturalist, localized names, Flickr photography, and Xeno-canto audio recordings.",
    close: "Close",
    infoSpeciesId: "Species ID",
    infoLastSeen: "Last seen",
    infoPhotos: "Photos",
    infoAvailable: "available",
    infoSounds: "Sounds",
    infoRecordings: "recordings",
    signOut: "Sign out",
    signIn: "Sign in with Google",
    loggedAs: "Signed in as",
    requireSignIn: "Please sign in to save and manage locations",
    saveCurrentRadar: "Save current radar position:",
    giveName: "Give it a name...",
    save: "⭐ Save",
    noSavedLocations: "No saved locations yet.",
    navigateSaveAbove: "Navigate to a spot and save it above!"
  },
  es: {
    activeRadar: "Radar Activo",
    radius: "radio",
    scanning: "Escaneando",
    speciesDetected: "especies detectadas",
    noSightings: "No se encontraron avistamientos",
    clickDifferent: "Haz clic en el mapa para escanear otra área.",
    radarRadius: "Radio del Radar",
    locating: "Obteniendo coordenadas...",
    clickScan: "Haz clic en el mapa para iniciar radar.",
    menuSettings: "Configuración",
    menuSaved: "Ubicaciones Guardadas",
    menuAbout: "Acerca de Birdetector",
    aboutSubtitle: "Radar Global de Aves PWA",
    aboutDesc: "Un radar de aves en tiempo real impulsado por el backend geoespacial de GBW. Descubre especies locales al instante con datos de eBird e iNaturalist, nombres localizados, fotografías de Flickr y grabaciones de audio de Xeno-canto.",
    close: "Cerrar",
    infoSpeciesId: "ID de Especie",
    infoLastSeen: "Visto por última vez",
    infoPhotos: "Fotos",
    infoAvailable: "disponibles",
    infoSounds: "Sonidos",
    infoRecordings: "grabaciones",
    signOut: "Cerrar sesión",
    signIn: "Iniciar sesión con Google",
    loggedAs: "Sesión iniciada como",
    requireSignIn: "Inicia sesión para guardar y administrar tus ubicaciones",
    saveCurrentRadar: "Guardar posición actual:",
    giveName: "Un nombre...",
    save: "⭐ Guardar",
    noSavedLocations: "Aún no hay ubicaciones guardadas.",
    navigateSaveAbove: "¡Navega a un lugar y guárdalo arriba!"
  },
  fr: {
    activeRadar: "Radar Actif",
    radius: "rayon",
    scanning: "Analyse",
    speciesDetected: "espèces détectées",
    noSightings: "Aucune observation trouvée",
    clickDifferent: "Cliquez sur la carte pour analyser une autre zone.",
    radarRadius: "Rayon du Radar",
    locating: "Localisation...",
    clickScan: "Cliquez n'importe où pour analyser le radar.",
    menuSettings: "Paramètres",
    menuSaved: "Lieux Enregistrés",
    menuAbout: "À propos de Birdetector",
    aboutSubtitle: "Radar Mondial des Oiseaux PWA",
    aboutDesc: "Un radar d'oiseaux en temps réel propulsé par le backend géo-spatial de GBW. Découvrez instantanément les espèces locales avec des données provenant d'eBird et d'iNaturalist, des noms localisés, des photographies Flickr et des enregistrements audio Xeno-canto.",
    close: "Fermer",
    infoSpeciesId: "ID d'espèce",
    infoLastSeen: "Dernière vue",
    infoPhotos: "Photos",
    infoAvailable: "disponibles",
    infoSounds: "Sons",
    infoRecordings: "enregistrements",
    signOut: "Déconnexion",
    signIn: "Se connecter avec Google",
    loggedAs: "Connecté en tant que",
    requireSignIn: "Veuillez vous connecter pour enregistrer et gérer vos lieux",
    saveCurrentRadar: "Enregistrer la position actuelle du radar:",
    giveName: "Donnez-lui un nom...",
    save: "⭐ Enregistrer",
    noSavedLocations: "Aucun lieu enregistré.",
    navigateSaveAbove: "Naviguez vers un lieu et enregistrez-le ci-dessus!"
  },
  pt: {
    activeRadar: "Radar Ativo",
    radius: "raio",
    scanning: "Analisando",
    speciesDetected: "espécies detectadas",
    noSightings: "Nenhum avistamento",
    clickDifferent: "Clique no mapa para analisar outra área.",
    radarRadius: "Raio do Radar",
    locating: "Localizando...",
    clickScan: "Clique em qualquer lugar para analisar o radar.",
    menuSettings: "Configurações",
    menuSaved: "Locais Salvos",
    menuAbout: "Sobre o Birdetector",
    aboutSubtitle: "Radar Global de Aves PWA",
    aboutDesc: "Um radar de aves em tempo real alimentado pelo backend geoespacial da GBW. Descubra espécies locais instantaneamente com dados do eBird e iNaturalist, nomes localizados, fotografias do Flickr e gravações de áudio do Xeno-canto.",
    close: "Fechar",
    infoSpeciesId: "ID da Espécie",
    infoLastSeen: "Visto por último",
    infoPhotos: "Fotos",
    infoAvailable: "disponíveis",
    infoSounds: "Sons",
    infoRecordings: "gravações",
    signOut: "Sair",
    signIn: "Entrar com Google",
    loggedAs: "Conectado como",
    requireSignIn: "Faça login para salvar e gerenciar seus locais",
    saveCurrentRadar: "Salvar a posição atual do radar:",
    giveName: "Dê um nome...",
    save: "⭐ Salvar",
    noSavedLocations: "Nenhum local salvo ainda.",
    navigateSaveAbove: "Navegue para um local e salve-o acima!"
  },
  de: {
    activeRadar: "Aktives Radar",
    radius: "radius",
    scanning: "Scannen",
    speciesDetected: "Arten entdeckt",
    noSightings: "Keine Sichtungen",
    clickDifferent: "Klicken Sie auf die Karte, um einen anderen Bereich zu scannen.",
    radarRadius: "Radar-Radius",
    locating: "Koordinaten werden ermittelt...",
    clickScan: "Klicken Sie auf die Karte, um zu scannen.",
    menuSettings: "Einstellungen",
    menuSaved: "Gespeicherte Orte",
    menuAbout: "Über Birdetector",
    aboutSubtitle: "Globales Vogelradar PWA",
    aboutDesc: "Ein Echtzeit-Vogelradar, das vom GBW-Geodaten-Backend angetrieben wird. Entdecken Sie lokale Arten sofort mit Daten von eBird und iNaturalist, lokalisierten Namen, Flickr-Fotografie und Xeno-canto-Audioaufnahmen.",
    close: "Schließen",
    infoSpeciesId: "Arten-ID",
    infoLastSeen: "Zuletzt gesehen",
    infoPhotos: "Fotos",
    infoAvailable: "verfügbar",
    infoSounds: "Töne",
    infoRecordings: "Aufnahmen",
    signOut: "Abmelden",
    signIn: "Mit Google anmelden",
    loggedAs: "Angemeldet als",
    requireSignIn: "Bitte anmelden, um Orte zu speichern und zu verwalten",
    saveCurrentRadar: "Aktuelle Radarposition speichern:",
    giveName: "Geben Sie einen Namen ein...",
    save: "⭐ Speichern",
    noSavedLocations: "Noch keine gespeicherten Orte.",
    navigateSaveAbove: "Navigieren Sie zu einem Ort und speichern Sie ihn oben!"
  },
  zh: {
    activeRadar: "主动雷达",
    radius: "半径",
    scanning: "扫描中",
    speciesDetected: "发现物种",
    noSightings: "未发现观测记录",
    clickDifferent: "点击地图扫描其他区域。",
    radarRadius: "雷达半径",
    locating: "正在定位...",
    clickScan: "点击地图任意位置进行雷达扫描。",
    menuSettings: "设置",
    menuSaved: "保存的位置",
    menuAbout: "关于 Birdetector",
    aboutSubtitle: "全球鸟类雷达 PWA",
    aboutDesc: "由 GBW 空间后台提供支持的实时观鸟雷达。结合 eBird 和 iNaturalist 的数据，通过本地化的名称、Flickr 摄影和 Xeno-canto 录音，即时发现当地物种。",
    close: "关闭",
    infoSpeciesId: "物种 ID",
    infoLastSeen: "最近一次观察",
    infoPhotos: "照片",
    infoAvailable: "可用",
    infoSounds: "声音",
    infoRecordings: "录音",
    signOut: "退出",
    signIn: "使用 Google 登录",
    loggedAs: "登录身份为",
    requireSignIn: "请登录以保存和管理位置",
    saveCurrentRadar: "保存当前雷达位置:",
    giveName: "给它起个名字...",
    save: "⭐ 保存",
    noSavedLocations: "暂无保存的位置。",
    navigateSaveAbove: "导航到一个地点并在上方保存！"
  }
};

export default function App() {
  const {
    currentLat, currentLng, radiusKm, language,
    surroundingBirds, isLoading,
    setLocation, setRadiusKm, setLanguage, fetchRadarData
  } = useBirdRadarStore();
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | null>(null);
  const [sliderValue, setSliderValue] = useState(radiusKm);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isSavedOpen, setIsSavedOpen] = useState(false);

  const { initAuth, user, loginWithGoogle, logout, isAuthLoading } = useAuthStore();
  const { appMode, setAppMode, tourLocations, selectedTourLocationId } = useTourStore();
  const { fetchSpeciesDetail } = useBirdRadarStore();

  const [selectedSpeciesDetail, setSelectedSpeciesDetail] = useState<RadarSpeciesDto | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const map = useMap();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);

  const t = UI_TEXT[language] || UI_TEXT["en"];

  // Derive unique families from the active radar birds
  const familyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    surroundingBirds.forEach(b => {
      const g = b.familyComName || t.other_families || "Other";
      counts[g] = (counts[g] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [surroundingBirds, t.other_families]);

  // Derive the filtered birds list
  const displayedBirds = useMemo(() => {
    return surroundingBirds.filter(b => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || 
        b.primaryCommonName.toLowerCase().includes(q) || 
        b.scientificName.toLowerCase().includes(q) ||
        (b.translations[language]?.toLowerCase() || "").includes(q);
      
      const famName = b.familyComName || t.other_families || "Other";
      const matchesFamily = !selectedFamily || famName === selectedFamily;
      
      return matchesSearch && matchesFamily;
    });
  }, [surroundingBirds, searchQuery, selectedFamily, language, t.other_families]);

  // Bootstrap Firebase auth listener once on mount
  useEffect(() => {
    const unsubscribe = initAuth();
    return unsubscribe;
  }, [initAuth]);

  // Scroll the species card into view when a marker is clicked
  useEffect(() => {
    if (selectedSpeciesId === null) return;
    const el = document.getElementById(`species-${selectedSpeciesId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Flash highlight
      el.style.transition = "box-shadow 0.3s";
      el.style.boxShadow = "0 0 0 3px #3b82f6";
      setTimeout(() => { el.style.boxShadow = ""; }, 1500);
    }
  }, [selectedSpeciesId]);

  const handleLocateUser = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setLocation(lat, lng);
          if (map) {
            map.panTo({ lat, lng });
            map.setZoom(14);
          }
        },
        (err) => {
          console.warn("Geolocation failed", err);
        },
        { enableHighAccuracy: true }
      );
    }
  }, [setLocation, map]);

  useEffect(() => {
    // 1. Get user coordinates precisely via HTML5 Geolocation on mount
    handleLocateUser();
  }, [handleLocateUser]);

  useEffect(() => {
    if (appMode === 'radar' && currentLat && currentLng) {
      fetchRadarData();
    }
  }, [currentLat, currentLng, radiusKm, fetchRadarData, appMode]);

  const handleSpeciesDetail = async (speciesId: number) => {
    setIsDetailLoading(true);
    const detail = await fetchSpeciesDetail(speciesId);
    setSelectedSpeciesDetail(detail);
    setIsDetailLoading(false);
  };

  const GOOGLE_MAPS_API_KEY = "AIzaSyBALtJkcwkI_8cN46O4oVWf8rCX5dNSAkM";

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="flex flex-col md:flex-row w-full h-full relative">
      {/* MAP PANE: full-width on mobile (45vh), 2/3 sidebar on desktop */}
      <div className="w-full h-[45vh] md:w-2/3 md:h-full relative flex-shrink-0">
        {currentLat && currentLng ? (
          <>
            <Map
              defaultCenter={{ lat: currentLat, lng: currentLng }}
              defaultZoom={13}
              mapId="gbw-radar-dark-map"
              disableDefaultUI={true}
              onClick={(e) => {
                if (e.detail.latLng) {
                  setLocation(e.detail.latLng.lat, e.detail.latLng.lng);
                }
              }}
            >
              {/* User position marker */}
              <AdvancedMarker position={{ lat: currentLat, lng: currentLng }}>
                <Pin background={'#ef4444'} borderColor={'#7f1d1d'} glyphColor={'white'} />
              </AdvancedMarker>

              {/* Dynamic Overlays */}
              {appMode === 'radar' ? (
                <RadarOverlays
                  lat={currentLat}
                  lng={currentLng}
                  radiusKm={radiusKm}
                  birds={displayedBirds}
                  onSelect={setSelectedSpeciesId}
                />
              ) : (
                <TargetOverlays 
                  locations={tourLocations} 
                  selectedLocationId={selectedTourLocationId} 
                  onSpeciesClick={handleSpeciesDetail}
                />
              )}
            </Map>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full bg-neutral-900">
            <span className="text-neutral-400">{t.locating}</span>
          </div>
        )}

        {/* Top-Left Floating Logo & Menu Button */}
        <div className="absolute top-4 left-4 z-40">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center gap-2 bg-black/80 backdrop-blur-md pl-1.5 pr-3 py-1.5 rounded-full shadow-xl border border-white/10 hover:bg-black/90 transition-all cursor-pointer relative"
          >
            <div className="relative">
              {user?.photoURL ? (
                <img src={user.photoURL} alt={user.displayName || "User"} className="w-11 h-11 object-cover rounded-full border border-white/20" />
              ) : (
                <img src="/logo.png" alt="Birdetector" className="w-11 h-11 object-cover rounded-full bg-white" />
              )}
              {/* Auth Status Indicator Dot */}
              <div className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-black ${user ? 'bg-green-500' : 'bg-neutral-500'}`} />
            </div>
            <span className="text-white/70 text-[10px]">▼</span>
          </button>

          {/* Slide-out Menu Dropdown */}
          {isMenuOpen && (
            <div className="absolute top-full left-0 mt-2 w-56 bg-black/90 backdrop-blur-xl border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col py-2 animate-in fade-in slide-in-from-top-2">
              {/* User Identity Header */}
              {user && (
                <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3 bg-white/5 mb-1">
                  {user.photoURL && <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full border border-white/10" />}
                  <div className="flex flex-col min-w-0">
                    <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">{t.loggedAs}</p>
                    <p className="text-xs text-white font-medium truncate">{user.displayName || user.email}</p>
                  </div>
                </div>
              )}

              <button 
                className="flex items-center gap-3 text-left px-5 py-3 text-sm text-neutral-300 hover:text-white hover:bg-white/10 transition border-b border-white/5 opacity-100 disabled:opacity-50"
                disabled={isAuthLoading}
                onClick={() => { 
                  setIsMenuOpen(false); 
                  setIsSavedOpen(true);
                }}
              >
                <span className="text-base w-5 flex justify-center">💾</span>
                <span>{t.menuSaved}</span>
              </button>
              
              <button 
                className="flex items-center gap-3 text-left px-5 py-3 text-sm text-indigo-400 hover:text-indigo-300 hover:bg-white/10 transition border-b border-white/5 font-bold"
                onClick={() => { setIsMenuOpen(false); setAppMode(appMode === 'tour' ? 'radar' : 'tour'); }}
              >
                <span className="text-base w-5 flex justify-center">{appMode === 'tour' ? "🎯" : "🏆"}</span>
                <span>{appMode === 'tour' ? "Switch to Radar Mode" : "Tour Planner"}</span>
              </button>

              {/* Login / Logout Option */}
              <button 
                className={`flex items-center gap-3 text-left px-5 py-3 text-sm transition border-b border-white/5 ${user ? 'text-red-400 hover:bg-red-500/10' : 'text-green-400 hover:bg-green-500/10 font-bold'}`}
                disabled={isAuthLoading}
                onClick={() => {
                  setIsMenuOpen(false);
                  if (user) logout();
                  else loginWithGoogle();
                }}
              >
                <span className="text-base w-5 flex justify-center">{user ? "🚪" : "🔑"}</span>
                <span>{isAuthLoading ? "..." : (user ? t.signOut : t.signIn)}</span>
              </button>
              <button 
                className="flex items-center gap-3 text-left px-5 py-3 text-sm text-neutral-300 hover:text-white hover:bg-white/10 transition"
                onClick={() => { setIsMenuOpen(false); setIsAboutOpen(true); }}
              >
                <span className="text-base w-5 flex justify-center">ℹ️</span>
                <span>{t.menuAbout}</span>
              </button>
            </div>
          )}
        </div>

        {/* Map Action Buttons (Locate Me) */}
        <div className="absolute top-20 right-4 z-20 flex flex-col gap-3">
          <button
            onClick={handleLocateUser}
            className="w-12 h-12 bg-black/80 backdrop-blur-md text-white rounded-full flex items-center justify-center border border-white/10 shadow-2xl hover:bg-black/90 hover:scale-110 transition-all cursor-pointer active:scale-95"
            title="Use My Location"
          >
            <span className="text-xl">🎯</span>
          </button>
        </div>

        {/* Language Selector */}
        <div className="hidden md:block absolute top-4 right-4 z-20">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full border border-neutral-700 outline-none hover:border-neutral-500 transition-colors shadow-lg text-sm appearance-none cursor-pointer"
            style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
            <option value="pt">Português</option>
            <option value="de">Deutsch</option>
            <option value="zh">中文</option>
          </select>
        </div>

        {/* Radius Slider — only visible in radar mode */}
        {appMode === 'radar' && (
        <div className="absolute bottom-0 md:bottom-6 left-0 md:left-1/2 md:-translate-x-1/2 z-10
          bg-black/80 backdrop-blur-sm px-5 py-3 md:rounded-2xl shadow-xl
          w-full md:w-72 border-t md:border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-neutral-300 flex items-center gap-1.5">
              <span>🎯</span> {t.radarRadius}
            </span>
            <span className="text-sm font-bold text-blue-400">
              {sliderValue < 1 ? `${Math.round(sliderValue * 1000)}m` : `${sliderValue.toFixed(1)}km`}
            </span>
          </div>
          <input
            type="range"
            min={0.5}
            max={50}
            step={0.5}
            value={sliderValue}
            onChange={(e) => setSliderValue(parseFloat(e.target.value))}
            onMouseUp={(e) => {
              const val = parseFloat((e.target as HTMLInputElement).value);
              setRadiusKm(val);
            }}
            onTouchEnd={(e) => {
              const val = parseFloat((e.target as HTMLInputElement).value);
              setRadiusKm(val);
            }}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #3b82f6 ${((sliderValue - 0.5) / 49.5) * 100}%, #374151 0%)`
            }}
          />
          <div className="flex justify-between text-[10px] text-neutral-600 mt-1.5">
            <span>500m</span><span>10km</span><span>20km</span><span>30km</span><span>40km</span><span>50km</span>
          </div>
        </div>
        )}

        {isLoading && (
          <div className="absolute inset-0 z-30 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-blue-500/5 backdrop-blur-[1px] transition-all duration-500" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] aspect-square bg-[conic-gradient(from_0deg,transparent_0%,rgba(59,130,246,0.2)_100%)] animate-spin duration-[3s] rounded-full" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-16 h-16 border-2 border-blue-400/30 rounded-full animate-ping" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full shadow-[0_0_20px_#3b82f6] animate-pulse" />
            </div>
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-md px-6 py-2 rounded-full border border-blue-500/30 text-white text-sm font-medium shadow-2xl flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
              {t.scanning}...
            </div>
          </div>
        )}

        {isLoading && (
          <div className="hidden md:block absolute bottom-6 right-6 bg-blue-600 px-4 py-2 rounded-full shadow-lg z-10 animate-pulse text-sm">
            {t.scanning} {radiusKm < 1 ? `${Math.round(radiusKm * 1000)}m` : `${radiusKm.toFixed(1)}km`}...
          </div>
        )}
      </div>

      {/* SIDEBAR PANE: full-width on mobile (flex-1 = remaining height), 1/3 sidebar on desktop */}
      <div className="w-full md:w-1/3 flex-1 md:h-full overflow-y-auto bg-neutral-950 flex flex-col border-t md:border-t-0 md:border-l border-neutral-800">
        {appMode === 'tour' ? (
           <TourPlannerPanel currentLat={currentLat} currentLng={currentLng} onSpeciesClick={handleSpeciesDetail} />
        ) : (
          <>
            <div className="sticky top-0 bg-neutral-950/90 backdrop-blur-sm border-b border-neutral-800 px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent flex items-center">
              {t.activeRadar}
            </h1>
            <div className="flex items-center gap-3">
              {/* Language Selector — visible only on mobile (desktop shows over map) */}
              <div className="block md:hidden">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-neutral-900 text-white px-2 py-1 rounded border border-neutral-700 outline-none text-xs appearance-none"
                  style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                >
                  <option value="en">EN</option>
                  <option value="es">ES</option>
                  <option value="fr">FR</option>
                  <option value="pt">PT</option>
                  <option value="de">DE</option>
                  <option value="zh">ZH</option>
                </select>
              </div>
              <span className="text-xs text-blue-400 font-semibold">
                {radiusKm < 1 ? `${Math.round(radiusKm * 1000)}m` : `${radiusKm.toFixed(1)}km`} {t.radius}
              </span>
            </div>
          </div>
          <p className="text-xs text-neutral-500 mt-0.5 mb-3">
            {isLoading
              ? `${t.scanning} ${radiusKm < 1 ? Math.round(radiusKm * 1000) + "m" : radiusKm.toFixed(1) + "km"}...`
              : `${displayedBirds.length} / ${surroundingBirds.length} ${t.speciesDetected} · ${currentLat?.toFixed(4)}, ${currentLng?.toFixed(4)}`
            }
          </p>

          {!isLoading && surroundingBirds.length > 0 && (
            <div className="flex flex-col gap-3">
              <input 
                type="text" 
                placeholder="Search species..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-neutral-900 border border-neutral-700 text-sm text-white rounded-full px-4 py-2 outline-none focus:border-blue-500 transition-colors"
              />
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                <button
                  onClick={() => setSelectedFamily(null)}
                  className={`flex-shrink-0 px-3 py-1 text-xs rounded-full border transition-all ${
                    selectedFamily === null 
                      ? "bg-blue-500 border-blue-500 text-white font-medium" 
                      : "bg-transparent border-neutral-700 text-neutral-400 hover:text-neutral-200"
                  }`}
                >
                  All Families
                </button>
                {familyCounts.map(([family, count]) => (
                  <button
                    key={family}
                    onClick={() => setSelectedFamily(family)}
                    className={`flex-shrink-0 px-3 py-1 text-xs rounded-full border transition-all ${
                      selectedFamily === family 
                        ? "bg-indigo-600 border-indigo-600 text-white font-medium shadow-lg shadow-indigo-500/20" 
                        : "bg-neutral-900 border-neutral-700 text-neutral-400 hover:text-neutral-200"
                    }`}
                  >
                    {family} <span className="opacity-50 ml-1">({count})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col gap-4">

          {surroundingBirds.length === 0 && !isLoading && (
            <div className="text-center p-10 bg-neutral-900/50 rounded-2xl border border-neutral-800">
              <div className="text-5xl mb-3">🌿</div>
              <p className="text-neutral-300 font-medium">{t.noSightings}</p>
              <p className="text-neutral-500 text-sm mt-1">{t.clickDifferent}</p>
            </div>
          )}

          {isLoading && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-neutral-400 text-sm">{t.scanning}...</p>
            </div>
          )}

          {!isLoading && displayedBirds.map((bird) => (
            <div key={bird.speciesId} id={`species-${bird.speciesId}`} className="rounded-2xl">
              <SpeciesCard bird={bird} />
            </div>
          ))}
          </div>
          </>
        )}
      </div>

      {/* About Modal */}
      {isAboutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in zoom-in-95 cursor-pointer" onClick={() => setIsAboutOpen(false)}>
          <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 rounded-3xl max-w-sm w-full shadow-2xl relative text-center cursor-default cursor-auto" onClick={e => e.stopPropagation()}>
            <img src="/logo.png" alt="Birdetector" className="w-20 h-20 mx-auto mb-4 object-cover rounded-full bg-white shadow-lg" />
            <h2 className="text-2xl font-bold text-white mb-1">Birdetector</h2>
            <p className="text-blue-400 text-sm font-semibold mb-3">{t.aboutSubtitle}</p>
            <p className="text-neutral-400 text-sm leading-relaxed mb-6">
              {t.aboutDesc}
            </p>
            <div className="text-[10px] text-neutral-600 mb-6 font-mono text-center">
              v1.0.0 &bull; open source
            </div>
            <button
              onClick={() => setIsAboutOpen(false)}
              className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition font-medium"
            >
              {t.close}
            </button>
          </div>
        </div>
      )}

      {/* Saved Locations Panel */}
      {isSavedOpen && <SavedLocationsPanel onClose={() => setIsSavedOpen(false)} />}

      {/* Species Detail Modal (Tour Mode) */}
      {selectedSpeciesDetail && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in"
          onClick={() => setSelectedSpeciesDetail(null)}
        >
          <div 
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl relative"
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={() => setSelectedSpeciesDetail(null)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-sm transition-colors"
            >
              ✕
            </button>
            <SpeciesCard bird={selectedSpeciesDetail} />
          </div>
        </div>
      )}

      {/* Loading Overlay for Detail Fetch */}
      {isDetailLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 backdrop-blur-[2px]">
          <div className="bg-neutral-900/90 p-4 rounded-2xl border border-white/10 shadow-2xl flex items-center gap-3">
             <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
             <span className="text-white text-sm font-medium">Loading details...</span>
          </div>
        </div>
      )}
    </div>
    </APIProvider>
  );
}
